import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { reservationsApi, CalendarLinks } from '../services/api';
import { Reservation, CreateReservationDto } from '../types';
import ReservationDetailModal from './ReservationDetailModal';

const Calendar: React.FC = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [newReservation, setNewReservation] = useState<CreateReservationDto>({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    isAllDay: false
  });
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdReservation, setCreatedReservation] = useState<Reservation | null>(null);
  const [calendarLinks, setCalendarLinks] = useState<CalendarLinks | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadReservations();
  }, []);

  const loadReservations = async () => {
    try {
      // Use getWithSubscriptions to include own reservations plus subscribed users' reservations
      const data = await reservationsApi.getWithSubscriptions();
      setReservations(data);
    } catch (error) {
      console.error('Failed to load reservations:', error);
    }
  };

  const createReservation = async () => {
    // Debug logging
    console.log('Form data:', {
      selectedDate,
      title: newReservation.title,
      isAllDay: newReservation.isAllDay,
      startTime: newReservation.startTime,
      endTime: newReservation.endTime
    });

    // Validation logic
    if (!newReservation.title) {
      alert('Please fill in the title field');
      return;
    }

    // For timed reservations, ensure we have times
    if (!newReservation.isAllDay) {
      if (!newReservation.startTime || !newReservation.endTime) {
        alert('Please fill in start time and end time for timed events');
        return;
      }
    }

    // Ensure we have a date for the reservation
    if (!selectedDate) {
      alert('Please select a date for the reservation');
      return;
    }

    try {
      let startDateTime: Date;
      let endDateTime: Date;

      if (newReservation.isAllDay) {
        // All-day reservation - use noon UTC to avoid timezone boundary issues
        if (!selectedDate) {
          alert('Please select a date for the reservation');
          return;
        }

        // Create dates at noon UTC to avoid timezone shifts
        const startYear = selectedDate.getFullYear();
        const startMonth = selectedDate.getMonth();
        const startDay = selectedDate.getDate();
        startDateTime = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));

        // If multi-day, use end date, otherwise same day
        const effectiveEndDate = endDate || selectedDate;
        const endYear = effectiveEndDate.getFullYear();
        const endMonth = effectiveEndDate.getMonth();
        const endDay = effectiveEndDate.getDate();
        // End at 23:59:59.999 UTC on the end date
        endDateTime = new Date(Date.UTC(endYear, endMonth, endDay, 23, 59, 59, 999));
      } else {
        // Timed reservation - can span multiple days
        // Use startDate if set, otherwise use selectedDate
        const startDateToUse = startDate || selectedDate!;
        // Use endDate if set, otherwise use startDateToUse (same day event)
        const endDateToUse = endDate || startDateToUse;
        
        startDateTime = new Date(startDateToUse);
        const [startHour, startMinute] = newReservation.startTime.split(':');
        startDateTime.setHours(parseInt(startHour), parseInt(startMinute));

        endDateTime = new Date(endDateToUse);
        const [endHour, endMinute] = newReservation.endTime.split(':');
        endDateTime.setHours(parseInt(endHour), parseInt(endMinute));
      }

      if (editingReservation) {
        // Update existing reservation
        await reservationsApi.update(editingReservation.id, {
          title: newReservation.title,
          description: newReservation.description,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          isAllDay: newReservation.isAllDay
        });
        closeModal();
        loadReservations();
      } else {
        // Create new reservation
        const created = await reservationsApi.create({
          title: newReservation.title,
          description: newReservation.description,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          isAllDay: newReservation.isAllDay
        });

        // Get calendar links for the new reservation
        const links = await reservationsApi.getCalendarLinks(created.id);

        setCreatedReservation(created);
        setCalendarLinks(links);
        closeModal();
        setShowSuccessModal(true);
        loadReservations();
      }
    } catch (error: any) {
      console.error('Failed to create reservation:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        alert(`Failed to create reservation: ${error.response.data.message || 'Unknown error'}`);
      } else {
        alert('Failed to create reservation: Network error');
      }
    }
  };

  const closeModal = () => {
    setShowReservationModal(false);
    setEditingReservation(null);
    setNewReservation({ title: '', description: '', startTime: '', endTime: '', isAllDay: false });
    setEndDate(null);
    setStartDate(null);
    setSelectedDate(null);
  };

  const openReservationDetail = (reservation: Reservation, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedReservation(reservation);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedReservation(null);
  };

  const handleDetailUpdate = () => {
    loadReservations();
    closeDetailModal();
  };

  const handleDetailDelete = async (id: string) => {
    try {
      await reservationsApi.delete(id);
      loadReservations();
      closeDetailModal();
    } catch (error: any) {
      console.error('Failed to delete reservation:', error);
      if (error.response) {
        alert(`Failed to delete reservation: ${error.response.data.message || 'Unknown error'}`);
      } else {
        alert('Failed to delete reservation: Network error');
      }
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getReservationsForDate = (date: Date) => {
    return reservations.filter(reservation => {
      const startDate = new Date(reservation.startTime);
      const endDate = new Date(reservation.endTime);

      // For all-day events, compare using UTC dates to avoid timezone issues
      if (reservation.isAllDay) {
        const checkYear = date.getFullYear();
        const checkMonth = date.getMonth();
        const checkDay = date.getDate();

        const startYear = startDate.getUTCFullYear();
        const startMonth = startDate.getUTCMonth();
        const startDay = startDate.getUTCDate();

        const endYear = endDate.getUTCFullYear();
        const endMonth = endDate.getUTCMonth();
        const endDay = endDate.getUTCDate();

        // Create comparable date numbers (YYYYMMDD format)
        const checkNum = checkYear * 10000 + checkMonth * 100 + checkDay;
        const startNum = startYear * 10000 + startMonth * 100 + startDay;
        const endNum = endYear * 10000 + endMonth * 100 + endDay;

        return checkNum >= startNum && checkNum <= endNum;
      }

      // For timed events, use local timezone comparison
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);

      const startDay = new Date(startDate);
      startDay.setHours(0, 0, 0, 0);

      const endDay = new Date(endDate);
      endDay.setHours(0, 0, 0, 0);

      // Check if the date falls within the reservation period
      return checkDate >= startDay && checkDate <= endDay;
    });
  };

  const getReservationPosition = (reservation: Reservation, date: Date) => {
    const startDate = new Date(reservation.startTime);
    const endDate = new Date(reservation.endTime);

    // For all-day events, use UTC dates to avoid timezone issues
    if (reservation.isAllDay) {
      const checkYear = date.getFullYear();
      const checkMonth = date.getMonth();
      const checkDay = date.getDate();

      const startYear = startDate.getUTCFullYear();
      const startMonth = startDate.getUTCMonth();
      const startDay = startDate.getUTCDate();

      const endYear = endDate.getUTCFullYear();
      const endMonth = endDate.getUTCMonth();
      const endDay = endDate.getUTCDate();

      const checkNum = checkYear * 10000 + checkMonth * 100 + checkDay;
      const startNum = startYear * 10000 + startMonth * 100 + startDay;
      const endNum = endYear * 10000 + endMonth * 100 + endDay;

      const isStart = checkNum === startNum;
      const isEnd = checkNum === endNum;
      const isMiddle = checkNum > startNum && checkNum < endNum;

      return { isStart, isEnd, isMiddle, isMultiDay: startNum !== endNum };
    }

    // For timed events, use local timezone
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);

    const endDay = new Date(endDate);
    endDay.setHours(0, 0, 0, 0);

    const isStart = checkDate.getTime() === startDay.getTime();
    const isEnd = checkDate.getTime() === endDay.getTime();
    const isMiddle = checkDate > startDay && checkDate < endDay;

    return { isStart, isEnd, isMiddle, isMultiDay: startDay.getTime() !== endDay.getTime() };
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const days = getDaysInMonth(currentDate);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">
            Welcome, {user?.firstName || user?.email}!
          </h1>
          <p className="text-blue-600 text-lg">
            Seamless reservation management made simple
          </p>
        </div>

        {/* Calendar Container */}
        <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-blue-200">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={prevMonth}
              className="p-3 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors duration-200 text-blue-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <h2 className="text-3xl font-bold text-slate-800">
              {formatDate(currentDate)}
            </h2>
            
            <button
              onClick={nextMonth}
              className="p-3 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors duration-200 text-blue-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {dayNames.map(day => (
              <div key={day} className="text-center py-3">
                <span className="text-blue-700 font-semibold text-sm">{day}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const isCurrentMonth = day && day.getMonth() === currentDate.getMonth();
              const isToday = day && day.toDateString() === new Date().toDateString();
              const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();
              const dayReservations = day ? getReservationsForDate(day) : [];

              return (
                <div
                  key={index}
                  className={`
                    min-h-[120px] p-2 rounded-xl border-2 transition-all duration-200 cursor-pointer
                    ${!day ? 'bg-transparent' : ''}
                    ${day && !isCurrentMonth ? 'bg-gray-50 border-gray-100' : ''}
                    ${day && isCurrentMonth && !isToday && !isSelected ? 'bg-white border-blue-100 hover:border-blue-300 hover:shadow-md' : ''}
                    ${isToday ? 'bg-blue-100 border-blue-400 shadow-lg' : ''}
                    ${isSelected ? 'bg-blue-200 border-blue-500 shadow-lg' : ''}
                  `}
                  onClick={() => {
                    if (day) {
                      setSelectedDate(day);
                      setShowReservationModal(true);
                    }
                  }}
                >
                  {day && (
                    <>
                      <div className="text-right mb-1">
                        <span className={`
                          text-sm font-medium
                          ${!isCurrentMonth ? 'text-gray-400' : ''}
                          ${isToday ? 'text-blue-800 font-bold' : 'text-gray-700'}
                        `}>
                          {day.getDate()}
                        </span>
                      </div>
                      
                      {/* Reservations */}
                      <div className="space-y-1">
                        {dayReservations.slice(0, 2).map(reservation => {
                          const position = getReservationPosition(reservation, day);
                          const roundedClass = position.isMultiDay 
                            ? position.isStart 
                              ? 'rounded-l rounded-r-none' 
                              : position.isEnd 
                                ? 'rounded-r rounded-l-none' 
                                : 'rounded-none'
                            : 'rounded';
                          
                          return (
                            <div
                              key={reservation.id}
                              className={`group relative text-xs p-1 truncate cursor-pointer ${roundedClass} ${
                                reservation.isAllDay
                                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-200'
                                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              } ${position.isMultiDay ? 'border-l-2 border-r-2' : ''}`}
                              title={`Click to view details`}
                              onClick={(e) => openReservationDetail(reservation, e)}
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate flex-1">
                                  {reservation.isAllDay && <span className="font-semibold hidden sm:inline">All Day </span>}
                                  {position.isMultiDay && position.isStart && <span className="font-semibold hidden sm:inline">Start </span>}
                                  {position.isMultiDay && position.isMiddle && <span className="font-semibold hidden sm:inline">... </span>}
                                  {position.isMultiDay && position.isEnd && <span className="font-semibold hidden sm:inline">End </span>}
                                  <span className="sm:hidden">
                                    {/* Mobile: show colored dot indicator */}
                                    <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                                      user?.id === reservation.userId ? 'bg-green-500' : 'bg-gray-500'
                                    }`}></span>
                                  </span>
                                  {reservation.title}
                                </span>
                                {/* Desktop: show owner indicator */}
                                <span className="hidden sm:inline-block ml-1 text-[10px] opacity-70">
                                  {user?.id === reservation.userId ? '(You)' : `(${reservation.user?.firstName?.charAt(0) || '?'})`}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {dayReservations.length > 2 && (
                          <div className="text-xs text-blue-600 text-center">
                            +{dayReservations.length - 2} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="mt-8 flex flex-wrap justify-center gap-3 sm:gap-4">
            <button
              onClick={() => {
                setSelectedDate(new Date()); // Set to today by default
                setShowReservationModal(true);
              }}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              Create Reservation
            </button>
            <button
              onClick={loadReservations}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-full font-semibold hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Reservation Modal */}
        {showReservationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">
                {editingReservation ? 'Edit Reservation' : 'Create Reservation'}
                {selectedDate && (
                  <span className="block text-lg text-blue-600 font-normal">
                    {selectedDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                )}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newReservation.title}
                    onChange={(e) => setNewReservation({...newReservation, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Reservation title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newReservation.description}
                    onChange={(e) => setNewReservation({...newReservation, description: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>

                {/* All Day Checkbox */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="allDay"
                    checked={newReservation.isAllDay}
                    onChange={(e) => {
                      setNewReservation({
                        ...newReservation, 
                        isAllDay: e.target.checked,
                        startTime: e.target.checked ? '' : newReservation.startTime,
                        endTime: e.target.checked ? '' : newReservation.endTime
                      });
                      if (!e.target.checked) {
                        setEndDate(null);
                      }
                    }}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="allDay" className="text-sm font-medium text-gray-700">
                    All Day Event
                  </label>
                </div>

                {/* Conditional End Date for All Day Events */}
                {newReservation.isAllDay && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date (Optional - for multi-day events)
                    </label>
                    <input
                      type="date"
                      value={endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}` : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          // Parse date parts directly to avoid timezone issues
                          const [year, month, day] = e.target.value.split('-').map(Number);
                          setEndDate(new Date(year, month - 1, day, 12, 0, 0)); // noon local time
                        } else {
                          setEndDate(null);
                        }
                      }}
                      min={selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : ''}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty for single-day event, or select an end date for multi-day reservation
                    </p>
                  </div>
                )}
                
                {/* Conditional Date/Time Fields for Timed Events */}
                {!newReservation.isAllDay && (
                  <>
                    {/* Start and End Dates for Multi-day Timed Events */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          value={startDate ? startDate.toISOString().split('T')[0] : (selectedDate ? selectedDate.toISOString().split('T')[0] : '')}
                          onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date *
                        </label>
                        <input
                          type="date"
                          value={endDate ? endDate.toISOString().split('T')[0] : (startDate ? startDate.toISOString().split('T')[0] : (selectedDate ? selectedDate.toISOString().split('T')[0] : ''))}
                          onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                          min={startDate ? startDate.toISOString().split('T')[0] : (selectedDate ? selectedDate.toISOString().split('T')[0] : '')}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Start and End Times */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time *
                        </label>
                        <input
                          type="time"
                          value={newReservation.startTime}
                          onChange={(e) => setNewReservation({...newReservation, startTime: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time *
                        </label>
                        <input
                          type="time"
                          value={newReservation.endTime}
                          onChange={(e) => setNewReservation({...newReservation, endTime: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      💡 For multi-day reservations: Set different start and end dates (e.g., Monday 17:00 to Friday 02:00)
                    </p>
                  </>
                )}
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createReservation}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  {editingReservation ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal with Calendar Links */}
        {showSuccessModal && createdReservation && calendarLinks && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">
                  Reservation Created!
                </h3>
                <p className="text-gray-600">
                  "{createdReservation.title}" has been successfully created.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700 text-center">
                  Add to your calendar:
                </p>

                {/* Google Calendar */}
                <a
                  href={calendarLinks.googleCalendarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-medium text-gray-700">Google Calendar</span>
                </a>

                {/* Outlook Calendar */}
                <a
                  href={calendarLinks.outlookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.356.228-.594.228h-8.106V6.583h8.106c.238 0 .436.076.594.228.158.152.238.346.238.576z"/>
                    <path fill="#0078D4" d="M15.062 17.669l-7.875 4.307V2.024l7.875 4.307v11.338z"/>
                    <path fill="#0078D4" d="M0 7.387c0-.23.08-.424.238-.576C.396 6.66.594 6.583.832 6.583H7.89v11.004H.832c-.238 0-.436-.076-.594-.228C.08 17.207 0 17.013 0 16.783V7.387z"/>
                  </svg>
                  <span className="font-medium text-gray-700">Outlook Calendar</span>
                </a>

                {/* Download ICS File */}
                <a
                  href={calendarLinks.icsUrl}
                  download
                  className="flex items-center justify-center w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="font-medium text-gray-700">Download .ics File</span>
                </a>
              </div>

              {/* Share Link Section */}
              <div className="border-t pt-4 mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Share this link with others:
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={calendarLinks.icsUrl}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 border border-gray-200 rounded-lg text-gray-600"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(calendarLinks.icsUrl);
                      alert('Link copied to clipboard!');
                    }}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Anyone with this link can add the event to their calendar
                </p>
              </div>

              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedReservation(null);
                  setCalendarLinks(null);
                }}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Reservation Detail Modal */}
        {showDetailModal && selectedReservation && (
          <ReservationDetailModal
            reservation={selectedReservation}
            onClose={closeDetailModal}
            onUpdate={handleDetailUpdate}
            onDelete={handleDetailDelete}
          />
        )}

      </div>
    </div>
  );
};

export default Calendar; 